import { UserInputError } from "./errors-CEDWDXew.js";
import { isNumericId } from "./slug-validation-MJ3jU0Hd.js";
import { ParamOrganizationSlug } from "./schema-DKEjeVqP.js";
import { defineTool } from "./define-SD6AQa4b.js";
import { apiServiceFromContext } from "./api-EaEPyKlQ.js";
import { get_monitor_details_default } from "./get-monitor-details-CP_Cj6P3.js";
import { formatToolCallInstruction } from "./tool-call-formatting-ALc98KqF.js";
import { resolveScopedOrganizationSlug, resolveScopedProjectSlug } from "./url-scope-CRM5sNo7.js";
import { get_issue_details_default } from "./get-issue-details-DHDUffp5.js";
import { get_trace_details_default } from "./get-trace-details-D7U8b5_V.js";
import { get_span_details_default } from "./get-span-details-DfXIT1v_.js";
import { parseSentryUrl } from "./url-helpers-CcAcmPuu.js";
import { get_replay_details_default } from "./get-replay-details-BWeEKq_j.js";
import { get_profile_details_default } from "./get-profile-details-B7TCia0B.js";
import { get_agent_conversation_details_default } from "./get-agent-conversation-details-DTJR8hFg.js";
import { get_snapshot_default } from "./get-snapshot-Ce1Z98WR.js";
import { get_snapshot_image_default } from "./get-snapshot-image-CWtW-DQz.js";
import { z } from "zod";
import { getActiveSpan, setTag } from "@sentry/core";

//#region src/tools/catalog/get-sentry-resource.ts
/** Types with full API integration. */
const FULLY_SUPPORTED_TYPES = [
	"issue",
	"event",
	"trace",
	"ai_conversation",
	"replay",
	"monitor",
	"snapshot"
];
function resolveResourceParams(params) {
	if (params.url) {
		const parsed = parseSentryUrl(params.url);
		return resolveFromParsedUrl(parsed, params);
	}
	if (!params.resourceType) throw new UserInputError("Either `url` or `resourceType` must be provided. Pass a `url` to auto-detect the resource type, or specify `resourceType` with `resourceId`.");
	if (!FULLY_SUPPORTED_TYPES.includes(params.resourceType)) throw new UserInputError(`Invalid resourceType: ${params.resourceType}. Supported types: ${FULLY_SUPPORTED_TYPES.join(", ")}`);
	if (!params.organizationSlug) throw new UserInputError("`organizationSlug` is required when not using a URL.");
	const resourceType = params.resourceType;
	const organizationSlug = params.organizationSlug;
	if (!params.resourceId) throw new UserInputError("`resourceId` is required when not using a URL.");
	const resourceId = params.resourceId;
	switch (resourceType) {
		case "issue": return {
			type: "issue",
			organizationSlug,
			issueId: resourceId.toUpperCase()
		};
		case "event": return {
			type: "event",
			organizationSlug,
			eventId: resourceId
		};
		case "trace": return {
			type: "trace",
			organizationSlug,
			traceId: resourceId
		};
		case "ai_conversation": return {
			type: "ai_conversation",
			organizationSlug,
			conversationId: resourceId
		};
		case "replay": return {
			type: "replay",
			organizationSlug,
			replayId: resourceId
		};
		case "monitor": return {
			type: "monitor",
			organizationSlug,
			monitorSlug: resourceId,
			projectSlugOrId: params.projectSlug ?? void 0
		};
		case "snapshot": return {
			type: "snapshot",
			organizationSlug,
			snapshotId: resourceId
		};
	}
}
/**
* When resourceType is provided alongside a URL, it can override a span-focused
* trace URL to fetch the full trace.
*/
function resolveFromParsedUrl(parsed, params) {
	const detectedType = parsed.type === "trace" && parsed.spanId ? "span" : parsed.type;
	const organizationSlug = resolveScopedOrganizationSlug({
		resourceLabel: "Sentry resource",
		scopedOrganizationSlug: params.organizationSlug,
		urlOrganizationSlug: parsed.organizationSlug
	});
	if (detectedType === "unknown") {
		if (parsed.transaction) throw new UserInputError(`Detected a performance summary URL for transaction "${parsed.transaction}". Use \`search_events\` to find traces and performance data for this transaction.`);
		throw new UserInputError("Could not determine resource type from URL. Supported URL patterns: issues, events, traces, agent conversations, profiles, replays, monitors, and releases.");
	}
	if (params.resourceType && params.resourceType !== detectedType) {
		if (params.resourceType === "trace" && detectedType === "span") {
			if (!parsed.traceId) throw new UserInputError("Could not extract trace ID from URL.");
			return {
				type: "trace",
				organizationSlug,
				traceId: parsed.traceId
			};
		}
		throw new UserInputError(`Cannot override URL type with resourceType '${params.resourceType}'. Only 'trace' on a span URL can be used as a resourceType override with a URL.`);
	}
	switch (detectedType) {
		case "issue":
			if (!parsed.issueId) throw new UserInputError("Could not extract issue ID from URL.");
			return {
				type: "issue",
				organizationSlug,
				issueId: parsed.issueId
			};
		case "event":
			if (!parsed.issueId || !parsed.eventId) throw new UserInputError("Could not extract issue ID and event ID from URL.");
			return {
				type: "event",
				organizationSlug,
				issueId: parsed.issueId,
				eventId: parsed.eventId
			};
		case "trace":
			if (!parsed.traceId) throw new UserInputError("Could not extract trace ID from URL.");
			return {
				type: "trace",
				organizationSlug,
				traceId: parsed.traceId
			};
		case "span":
			if (!parsed.traceId || !parsed.spanId) throw new UserInputError("Could not extract trace ID and span ID from URL.");
			return {
				type: "span",
				organizationSlug,
				traceId: parsed.traceId,
				spanId: parsed.spanId
			};
		case "ai_conversation":
			if (!parsed.conversationId) throw new UserInputError("Could not extract agent conversation ID from URL.");
			return {
				type: "ai_conversation",
				organizationSlug,
				conversationId: parsed.conversationId,
				projectSlugOrId: parsed.projectSlugOrId,
				start: parsed.start,
				end: parsed.end
			};
		case "profile":
			if (!parsed.projectSlugOrId) throw new UserInputError("Could not extract project slug from profile URL.");
			return {
				type: "profile",
				organizationSlug,
				projectSlugOrId: resolveScopedProjectSlug({
					resourceLabel: "Profile",
					scopedProjectSlug: params.projectSlug,
					urlProjectSlug: parsed.projectSlugOrId
				}),
				profileId: parsed.profileId,
				profilerId: parsed.profilerId,
				start: parsed.start,
				end: parsed.end
			};
		case "replay":
			if (!parsed.replayId) throw new UserInputError("Could not extract replay ID from URL.");
			return {
				type: "replay",
				organizationSlug,
				replayId: parsed.replayId
			};
		case "monitor":
			if (!parsed.monitorSlug) throw new UserInputError("Could not extract monitor slug from URL.");
			return {
				type: "monitor",
				organizationSlug,
				monitorSlug: parsed.monitorSlug,
				projectSlugOrId: parsed.projectSlugOrId ? isNumericId(parsed.projectSlugOrId) ? parsed.projectSlugOrId : resolveScopedProjectSlug({
					resourceLabel: "Monitor",
					scopedProjectSlug: params.projectSlug,
					urlProjectSlug: parsed.projectSlugOrId
				}) : void 0
			};
		case "release":
			if (!parsed.releaseVersion) throw new UserInputError("Could not extract release version from URL.");
			return {
				type: "release",
				organizationSlug,
				releaseVersion: parsed.releaseVersion
			};
		case "snapshot":
			if (!parsed.snapshotId) throw new UserInputError("Could not extract snapshot ID from URL.");
			return {
				type: "snapshot",
				organizationSlug,
				snapshotId: parsed.snapshotId,
				selectedSnapshot: parsed.selectedSnapshot
			};
	}
}
function assertCatalogToolAvailable(context, toolName, resourceLabel) {
	if (!isToolAvailable(toolName, context.availableToolNames)) throw new UserInputError(`${resourceLabel} resources require the inspect skill. Enable inspect tools or call ${toolName} in a session where it is available.`);
}
function isToolAvailable(toolName, availableToolNames) {
	return !availableToolNames || availableToolNames.has(toolName);
}
function generateUnsupportedResourceMessage(resolved, apiService, experimentalMode, availableToolNames, directToolNames) {
	const { type, organizationSlug } = resolved;
	switch (type) {
		case "release": {
			const releaseUrl = apiService.getReleaseUrl(organizationSlug, resolved.releaseVersion ?? "");
			const findReleasesInstruction = formatToolCallInstruction({
				toolName: "find_releases",
				arguments: { organizationSlug },
				experimentalMode,
				availableToolNames,
				directToolNames,
				fallbackInstruction: "Release listing is not available in this session",
				purpose: "to list releases and their details"
			});
			return [
				"# Release Detected",
				"",
				`**Organization**: ${organizationSlug}`,
				`**Release**: ${resolved.releaseVersion}`,
				"",
				"To get release information:",
				"",
				`- **View in Sentry**: [Open Release](${releaseUrl})`,
				`- **Find releases**: ${findReleasesInstruction}`,
				`- **Search issues**: Use \`search_issues\` with query \`release:${resolved.releaseVersion}\` to find issues in this release`
			].join("\n");
		}
		default: return `Unsupported resource type: ${type}`;
	}
}
var get_sentry_resource_default = defineTool({
	name: "get_sentry_resource",
	skills: [
		"inspect",
		"triage",
		"seer"
	],
	requiredScopes: ["event:read", "project:read"],
	description: ({ experimentalMode, availableToolNames, directToolNames }) => {
		const monitorResourcesAvailable = isToolAvailable("get_monitor_details", availableToolNames);
		const fullResolutionInstruction = formatToolCallInstruction({
			toolName: "get_snapshot_image",
			arguments: {
				organizationSlug: "<organization_slug>",
				snapshotId: "<snapshot_id>",
				imageIdentifier: "<image_file_name>",
				imageResolution: "full"
			},
			experimentalMode,
			availableToolNames,
			directToolNames,
			fallbackInstruction: "Full-resolution snapshot image bytes are not available in this session",
			purpose: "for full-resolution image bytes"
		});
		const supportedResources = monitorResourcesAvailable ? "issues, events, traces, spans, agent conversations, replays, monitors, preprod snapshots, and snapshot images." : "issues, events, traces, spans, agent conversations, replays, preprod snapshots, and snapshot images.";
		const resourceIds = [...monitorResourcesAvailable ? ["- monitor: <monitorSlug>"] : [], "- snapshot: <snapshotId>"];
		return [
			"Fetch a Sentry resource by URL, or by resourceType plus resourceId.",
			"Pass a Sentry URL directly when possible; the resource type is auto-detected.",
			"",
			`Supports ${supportedResources}`,
			"Trace lookups return a condensed overview by default.",
			"",
			"Agent Conversations: A conversation is a set of spans sharing the same gen_ai.conversation.id. Use resourceType='ai_conversation' with a conversation ID, or pass a Sentry conversation URL, to fetch the transcript/details. To discover or list conversations, use search_agent_conversations. Conversations are NOT issues — do not use search_issues for conversation queries.",
			"",
			"For preprod snapshot URLs (matching 'sentry.io/preprod/snapshots/'):",
			"- Without ?selectedSnapshot=: returns the snapshot diff summary (changed, added, removed images)",
			`- With ?selectedSnapshot=<image_file_name>: returns the image preview and metadata. ${fullResolutionInstruction}.`,
			"",
			"Resource IDs:",
			...resourceIds,
			"",
			"<examples>",
			"get_sentry_resource(url='https://sentry.io/issues/PROJECT-123/')",
			"get_sentry_resource(resourceType='issue', organizationSlug='my-org', resourceId='PROJECT-123')",
			"get_sentry_resource(resourceType='ai_conversation', organizationSlug='my-org', resourceId='conversation-123')",
			"get_sentry_resource(url='https://sentry.sentry.io/preprod/snapshots/123/')",
			"get_sentry_resource(url='https://sentry.sentry.io/preprod/snapshots/123/?selectedSnapshot=login_screen.png')",
			"</examples>"
		].join("\n");
	},
	inputSchema: {
		url: z.string().url().optional().describe("Sentry URL. The resource type is auto-detected from the URL pattern."),
		resourceType: z.enum([
			"issue",
			"event",
			"trace",
			"ai_conversation",
			"replay",
			"monitor",
			"snapshot"
		]).optional().describe("Resource type. With a URL, can override a span-focused trace URL with `trace`. Use `monitor` with a monitor slug only when inspect monitor tools are available or `snapshot` with a snapshot artifact ID."),
		resourceId: z.string().trim().optional().describe("Resource identifier: issue shortId (e.g., 'PROJECT-123'), event ID, trace ID, agent conversation ID, replay ID, monitor slug when inspect monitor tools are available, or snapshot artifact ID. Required when not using a URL."),
		organizationSlug: ParamOrganizationSlug.optional()
	},
	annotations: {
		readOnlyHint: true,
		destructiveHint: false,
		openWorldHint: true
	},
	async handler(params, context) {
		const resolved = resolveResourceParams({
			url: params.url,
			resourceType: params.resourceType,
			resourceId: params.resourceId,
			organizationSlug: params.organizationSlug ?? context.constraints.organizationSlug,
			projectSlug: context.constraints.projectSlug
		});
		setTag("resource.type", resolved.type);
		setTag("organization.slug", resolved.organizationSlug);
		if (resolved.spanId) setTag("trace.span_id", resolved.spanId);
		getActiveSpan()?.setAttribute("app.resource.type", resolved.type);
		if (resolved.type === "release") {
			const apiService = apiServiceFromContext(context, { regionUrl: context.constraints.regionUrl ?? void 0 });
			return generateUnsupportedResourceMessage(resolved, apiService, context.experimentalMode ?? false, context.availableToolNames, context.directToolNames);
		}
		switch (resolved.type) {
			case "issue": return get_issue_details_default.handler({
				organizationSlug: resolved.organizationSlug,
				issueId: resolved.issueId,
				regionUrl: context.constraints.regionUrl ?? null
			}, context);
			case "event": return get_issue_details_default.handler({
				organizationSlug: resolved.organizationSlug,
				issueId: resolved.issueId,
				eventId: resolved.eventId,
				regionUrl: context.constraints.regionUrl ?? null
			}, context);
			case "trace": return get_trace_details_default.handler({
				organizationSlug: resolved.organizationSlug,
				traceId: resolved.traceId,
				regionUrl: context.constraints.regionUrl ?? null
			}, context);
			case "span": return get_span_details_default.handler({
				organizationSlug: resolved.organizationSlug,
				traceId: resolved.traceId,
				spanId: resolved.spanId,
				regionUrl: context.constraints.regionUrl ?? null
			}, context);
			case "ai_conversation": return get_agent_conversation_details_default.handler({
				organizationSlug: resolved.organizationSlug,
				conversationId: resolved.conversationId,
				project: resolved.projectSlugOrId,
				start: resolved.start,
				end: resolved.end,
				regionUrl: context.constraints.regionUrl ?? void 0
			}, context);
			case "replay": return get_replay_details_default.handler({
				replayUrl: params.url,
				organizationSlug: resolved.organizationSlug,
				replayId: resolved.replayId,
				regionUrl: context.constraints.regionUrl ?? void 0
			}, context);
			case "monitor":
				assertCatalogToolAvailable(context, "get_monitor_details", "Monitor");
				return get_monitor_details_default.handler({
					organizationSlug: resolved.organizationSlug,
					projectSlugOrId: resolved.projectSlugOrId ?? null,
					monitorSlug: resolved.monitorSlug,
					regionUrl: context.constraints.regionUrl ?? null,
					environment: null,
					period: "24h",
					start: null,
					end: null,
					checkInLimit: 10,
					includeStats: true,
					rollupSeconds: null
				}, context);
			case "profile": return get_profile_details_default.handler({
				profileUrl: params.url,
				organizationSlug: resolved.organizationSlug,
				projectSlugOrId: resolved.projectSlugOrId,
				profileId: resolved.profileId,
				profilerId: resolved.profilerId,
				start: resolved.start,
				end: resolved.end,
				regionUrl: context.constraints.regionUrl ?? null,
				focusOnUserCode: true
			}, context);
			case "snapshot":
				if (resolved.selectedSnapshot) return get_snapshot_image_default.handler({
					organizationSlug: resolved.organizationSlug,
					snapshotId: resolved.snapshotId,
					imageIdentifier: resolved.selectedSnapshot,
					imageResolution: "preview",
					regionUrl: context.constraints.regionUrl ?? null
				}, context);
				return get_snapshot_default.handler({
					organizationSlug: resolved.organizationSlug,
					snapshotId: resolved.snapshotId,
					showUnmodified: false,
					regionUrl: context.constraints.regionUrl ?? null
				}, context);
			default: {
				const _exhaustiveCheck = resolved.type;
				throw new Error(`Unhandled resource type: ${_exhaustiveCheck}`);
			}
		}
	}
});

//#endregion
export { FULLY_SUPPORTED_TYPES, get_sentry_resource_default, resolveResourceParams };
//# sourceMappingURL=get-sentry-resource-DAx17Rhc.js.map